import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  Cloud as CloudIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAppStore } from '@/store/useAppStore';
import { zohoAuthService } from '@/services/zohoAuth';
import { zohoApiService } from '@/services/zohoApi';

export const ZohoLogin: React.FC = () => {
  const { auth, setAuth, clearAuth, setLoading, setError } = useAppStore();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionTest, setConnectionTest] = useState<boolean | null>(null);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const savedAuth = zohoAuthService.loadAuth();
    if (savedAuth) {
      setAuth(savedAuth);
    }
  }, [setAuth]);

  // Tester la connexion API
  const testConnection = async () => {
    if (!auth) return;
    
    setLoading(true);
    try {
      const isConnected = await zohoApiService.testConnection();
      setConnectionTest(isConnected);
    } catch (error) {
      setConnectionTest(false);
      setError({
        code: 'CONNECTION_ERROR',
        message: 'Erreur de connexion à l\'API Zoho',
        details: error
      });
    } finally {
      setLoading(false);
    }
  };

  // Se connecter à Zoho
  const handleLogin = () => {
    setIsConnecting(true);
    const authUrl = zohoAuthService.getAuthUrl();
    window.location.href = authUrl;
  };

  // Se déconnecter
  const handleLogout = () => {
    zohoAuthService.clearAuth();
    clearAuth();
    setConnectionTest(null);
  };

  // Traiter le callback d'authentification
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    if (code && !auth) {
      setIsConnecting(true);
      zohoAuthService.exchangeCodeForToken(code)
        .then((authData) => {
  
          zohoAuthService.saveAuth(authData);
          setAuth(authData);
          // Nettoyer l'URL
          window.history.replaceState({}, document.title, window.location.pathname);
        })
        .catch((error) => {
          console.error('Auth error:', error);
          setError({
            code: 'AUTH_ERROR',
            message: 'Erreur lors de l\'authentification',
            details: error instanceof Error ? error.message : String(error)
          });
        })
        .finally(() => {
          setIsConnecting(false);
        });
    } else if (error) {
      setError({
        code: 'AUTH_ERROR',
        message: 'Erreur d\'autorisation Zoho',
        details: error
      });
    }
  }, [auth, setAuth, setError]);

  if (isConnecting) {
    return (
      <Card sx={{ borderRadius: 3, mb: 4 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Connexion en cours...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirection vers Zoho pour l'authentification
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3, mb: 4 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudIcon color="primary" />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              Connexion Zoho
            </Typography>
          </Box>
        }
        subheader="Connectez-vous à votre compte Zoho pour accéder aux forums"
      />
      
      <CardContent>
        {!auth ? (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2">
                Authentification requise
              </Typography>
              <Typography variant="body2">
                Pour utiliser ZohoMailer, vous devez vous connecter à votre compte Zoho.
                Cela permettra d'accéder à vos forums et d'envoyer des messages.
              </Typography>
            </Alert>

            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CloudIcon />}
                onClick={handleLogin}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Se connecter avec Zoho
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" gutterBottom>
              Permissions requises :
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label="Lecture des forums" size="small" variant="outlined" />
              <Chip label="Création de topics" size="small" variant="outlined" />
              <Chip label="Upload de fichiers" size="small" variant="outlined" />
              <Chip label="Lecture des utilisateurs" size="small" variant="outlined" />
            </Box>
          </Box>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {auth.userPhoto ? (
                  <Box
                    component="img"
                    src={auth.userPhoto}
                    alt="Photo de profil"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '2px solid #fff'
                    }}
                  />
                ) : (
                  <CheckIcon />
                )}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {auth.userName || auth.userFirstName || auth.userEmail || 'Utilisateur Zoho'}
                  </Typography>
                  {auth.userEmail && (
                    <Typography variant="body2" color="text.secondary">
                      {auth.userEmail}
                    </Typography>
                  )}
                  {auth.userFirstName && auth.userLastName && (
                    <Typography variant="body2" color="text.secondary">
                      {auth.userFirstName} {auth.userLastName}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={testConnection}
                disabled={connectionTest === null}
              >
                Tester la connexion
              </Button>
              
              <Button
                variant="outlined"
                color="error"
                onClick={handleLogout}
              >
                Se déconnecter
              </Button>
            </Box>

            {connectionTest !== null && (
              <Alert 
                severity={connectionTest ? 'success' : 'error'}
                icon={connectionTest ? <CheckIcon /> : <ErrorIcon />}
                sx={{ mb: 2 }}
              >
                {connectionTest 
                  ? 'Connexion à l\'API Zoho réussie'
                  : 'Erreur de connexion à l\'API Zoho'
                }
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary">
              Token valide jusqu'au {auth.expiresAt instanceof Date ? auth.expiresAt.toLocaleDateString('fr-FR') : 'Date inconnue'} à {auth.expiresAt instanceof Date ? auth.expiresAt.toLocaleTimeString('fr-FR') : ''}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}; 